import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Check, ArrowLeft, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const { user, hasActivePlan } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const plans = [
    { id: 'free', name: 'Gratis', price: '$0', period: '', desc: 'Explora el modelo demo', features: ['Visualizar proyecto demo completo', 'Dashboard con KPIs', 'Crear proyectos manualmente', 'Sin acceso a modulos avanzados', 'Sin exportaciones ni IA'], free: true },
    { id: 'monthly', name: 'Mensual', price: '$14', period: 'USD/mes', desc: 'Analista IA + todos los modulos', features: ['Proyectos ilimitados', 'Analista IA incluido', '10 generaciones con IA/mes', '10 modificaciones con IA/mes', 'Consultas con IA ilimitadas', 'Exportar PDF y Excel', 'Soporte prioritario'] },
    { id: 'annual', name: 'Anual', price: '$67', period: 'USD/año', desc: 'Mejor valor — ahorra 60%', features: ['Todo del plan mensual', '10 generaciones con IA/mes', '10 modificaciones con IA/mes', 'Consultas con IA ilimitadas', 'Modelo Excel tipo Banca de Inversion', '12 meses por $67', 'Soporte VIP'], popular: true },
  ];

  const handleCheckout = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoadingPlan(planId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API}/api/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear sesion de pago');
    } finally {
      setLoadingPlan(null);
    }
  };

  if (hasActivePlan()) {
    const expires = new Date(user.plan_expires_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Plan Activo</h1>
          <p className="text-muted-foreground mb-1">Tu plan esta activo hasta:</p>
          <p className="text-lg font-semibold text-emerald-400 mb-6">{expires}</p>

          {/* Pack Extra upsell */}
          <Card className="border-amber-500/30 bg-amber-500/5 mb-6 text-left">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Pack Extra IA</h3>
                  <p className="text-xs text-muted-foreground">20 generaciones + 20 modificaciones adicionales</p>
                </div>
                <span className="ml-auto text-lg font-bold">$10 <span className="text-xs font-normal text-muted-foreground">USD</span></span>
              </div>
              <Button
                onClick={() => handleCheckout('pack_extra')}
                disabled={loadingPlan === 'pack_extra'}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                data-testid="checkout-pack-extra-btn"
              >
                {loadingPlan === 'pack_extra' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                {loadingPlan === 'pack_extra' ? 'Redirigiendo...' : 'Comprar Pack Extra'}
              </Button>
            </CardContent>
          </Card>

          <Button onClick={() => navigate('/app')} className="bg-gradient-to-r from-blue-500 to-cyan-500">Ir a la App</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate('/app')} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>

        <div className="text-center mb-16">
          <h1 className="text-3xl font-bold mb-4">Elige tu plan</h1>
          <p className="text-muted-foreground">Acceso completo a todas las herramientas de evaluacion financiera</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={`relative border-border/40 ${plan.popular ? 'border-blue-500 ring-1 ring-blue-500/20' : ''}`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-medium rounded-full">
                  Mas Popular
                </div>
              )}
              <CardContent className="pt-8 pb-8">
                <h3 className="font-semibold text-lg mb-1">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground ml-1 text-sm">/{plan.period}</span>}
                </div>
                <Button 
                  onClick={() => plan.free ? navigate('/app') : handleCheckout(plan.id)} 
                  disabled={loadingPlan === plan.id}
                  className={`w-full mb-6 ${plan.popular ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  data-testid={`checkout-${plan.id}-btn`}
                >
                  {loadingPlan === plan.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {plan.free ? 'Acceder Gratis' : (loadingPlan === plan.id ? 'Redirigiendo...' : 'Seleccionar')}
                </Button>
                <ul className="space-y-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pack Extra IA add-on */}
        {user && (
          <div className="mt-12 max-w-lg mx-auto">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Pack Extra IA</h3>
                    <p className="text-sm text-muted-foreground">Necesitas mas capacidad? Recarga al instante.</p>
                  </div>
                  <span className="ml-auto text-2xl font-bold">$10 <span className="text-sm font-normal text-muted-foreground">USD</span></span>
                </div>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-amber-400 shrink-0" />+20 generaciones de proyectos con IA</li>
                  <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-amber-400 shrink-0" />+20 modificaciones de proyectos con IA</li>
                  <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-amber-400 shrink-0" />Se acumulan con tu plan actual</li>
                </ul>
                <Button
                  onClick={() => handleCheckout('pack_extra')}
                  disabled={loadingPlan === 'pack_extra'}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="checkout-pack-extra-pricing-btn"
                >
                  {loadingPlan === 'pack_extra' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  {loadingPlan === 'pack_extra' ? 'Redirigiendo...' : 'Comprar Pack Extra'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
